import { useEffect, useReducer, useRef, useCallback } from "react";
import { type TabData } from "@utils/dashboard-storage";
import { UseTabManagementProps } from "./types";
import { tabsReducer, initialState } from "./reducer";
import { validateTabName, trim, tempId } from "./utils";

export const useTabsState = (props: UseTabManagementProps) => {
  const [state, dispatch] = useReducer(tabsReducer, props.tabs, initialState);
  const newTabInputRef = useRef<HTMLInputElement>(null);
  const dragCounter = useRef(0);
  const propsTabsRef = useRef(props.tabs);

  // sync from props (e.g., when parent adds a server-created tab)
  useEffect(() => {
    // Only sync if props reference actually changed
    if (props.tabs !== propsTabsRef.current) {
      propsTabsRef.current = props.tabs;
      dispatch({ type: "SYNC_FROM_PROPS", tabs: props.tabs });
    }
  }, [props.tabs]);

  // add flow
  const startAdd = useCallback(() => {
    if (state.localTabs.length < 20) dispatch({ type: "START_ADD" });
  }, [state.localTabs.length]);

  const cancelAdd = useCallback(() => {
    dispatch({ type: "CANCEL_ADD" });
  }, []);

  const setNewTabName = useCallback((value: string) => {
    dispatch({ type: "SET_NEW_TAB_NAME", value });
  }, []);

  const addOptimistic = useCallback((label: string) => {
    const err = validateTabName(label);
    if (err) {
      dispatch({ type: "SET_NEW_TAB_ERROR", value: err });
      return null;
    }
    const tab: TabData = { id: tempId(), label: trim(label) };
    dispatch({ type: "ADD_OPTIMISTIC_TAB", tab });
    return tab;
  }, []);

  // rename + blur validation
  const updateLabel = useCallback((id: string, label: string) => {
    dispatch({ type: "UPDATE_TAB_NAME", id, label });
  }, []);

  const validateOnBlur = useCallback(
    (id: string, label: string, original?: string) => {
      const err = validateTabName(label);
      if (err) {
        dispatch({ type: "SET_TAB_ERROR", id, message: err });
        if (typeof original === "string")
          dispatch({ type: "REVERT_TAB_NAME", id, label: original });
      } else {
        dispatch({ type: "CLEAR_TAB_ERROR", id });
      }
    },
    [],
  );

  // hover
  const hoverTab = useCallback(
    (tabId: string | null) => dispatch({ type: "HOVER_TAB", tabId }),
    [],
  );
  const hoverNewItem = useCallback(
    (value: boolean) => dispatch({ type: "HOVER_NEW_ITEM", value }),
    [],
  );

  // dragging
  const dragStart = useCallback(
    (index: number) => dispatch({ type: "DRAG_START", index }),
    [],
  );
  const dragEnd = useCallback(() => {
    dragCounter.current = 0;
    dispatch({ type: "DRAG_END" });
  }, []);
  const dragOver = useCallback(
    (index: number | null) => dispatch({ type: "DRAG_OVER", index }),
    [],
  );
  const dragEnter = useCallback((index: number) => {
    dragCounter.current++;
    dispatch({ type: "DRAG_OVER", index });
  }, []);
  const dragLeave = useCallback(() => {
    dragCounter.current--;
    if (dragCounter.current === 0) dispatch({ type: "DRAG_OVER", index: null });
  }, []);
  const reorder = useCallback(
    (from: number, to: number) => dispatch({ type: "REORDER", from, to }),
    [],
  );

  // deletion
  const openDeleteConfirm = useCallback(
    (tabId: string, tabName: string, hasContent = false) => {
      dispatch({
        type: "OPEN_DELETE_CONFIRM",
        payload: { tabId, tabName, hasContent },
      });
    },
    [],
  );
  const closeDeleteConfirm = useCallback(
    () => dispatch({ type: "CLOSE_DELETE_CONFIRM" }),
    [],
  );
  const setDeleteHasContent = useCallback(
    (hasContent: boolean) =>
      dispatch({ type: "SET_DELETE_HAS_CONTENT", hasContent }),
    [],
  );
  const removeLocal = useCallback(
    (tabId: string) => dispatch({ type: "REMOVE_TAB", tabId }),
    [],
  );

  return {
    state,
    refs: { newTabInputRef, dragCounter },
    actions: {
      startAdd,
      cancelAdd,
      setNewTabName,
      addOptimistic,
      updateLabel,
      validateOnBlur,
      hoverTab,
      hoverNewItem,
      dragStart,
      dragEnd,
      dragOver,
      dragEnter,
      dragLeave,
      reorder,
      openDeleteConfirm,
      closeDeleteConfirm,
      setDeleteHasContent,
      removeLocal,
    },
  } as const;
};
